import { put, delay, takeLatest, select } from "@redux-saga/core/effects";
import { approveBooking, cancelBooking, confirmBooking, createBookingComment, deleteBookingComment, deleteBookingHold, fetchAllAmenities, fetchAllBookings, fetchBlockedSlots, fetchBookingById, fetchBookingComments, holdBooking, rejectBooking, updateBooking, updateBookingHold } from "../../../Utils/BackendAPICalls";
import { Actions, Types } from "../../Reducer/rootReducer";
import { fetchUnit } from "../../selectors";
import { BookingStatus, BookingType, Comment, HoldBooking, Unit } from "../../../Types/UnitFeatureTypes";

interface FetchAmenitiesActions {
    payload: {
        id?: string;
        offset?: number;
    };
}

interface HoldBookingActions {
    payload: {
        bookingData: HoldBooking;
    };
}

interface UpdateBooking {
    payload: {
        bookingData: {
            id: number;
            amenity_id: number;
            slots?: Date[];
            booking_start: Date;
            booking_end?: Date;
        }
    }
}

interface FetchBookingAction {
    payload: {
        id?: number;
        offset?: number;
        sortColumn?: string;
        sortDirection?: string;
    };
}

interface BookingAction {
    payload: {
        id: number;
    };
}

interface FetchBlockedSlots {
    payload: {
        amenity_id: number;
        start_date: string;
    };
}

interface ApproveBookingAction {
    payload: {
        booking_id: number;
    };
}

interface BookingComment {
    payload: {
        comment: Comment;
    };
}

interface DeleteBookingComment {
    payload: {
        comment_id: number;
    };
}

export function* fetchBookingRequestFunction(action: FetchBookingAction): Generator<any, void, Response> {
    const { id, offset, sortColumn, sortDirection } = action.payload;
    let response: any = null;
    try {
        yield delay(100);
        if (id) {
            response = yield fetchBookingById(id);
        } else {
            response = yield fetchAllBookings(offset ?? 0, sortColumn ?? 'created_on', sortDirection ?? 'desc');
        }
        if (!response || response?.error) {
            yield put(Actions.fetchBooking.bookingActions.failure({ message: response.error }));
        } else {
            yield put(Actions.fetchBooking.bookingActions.success({ bookings: response.bookings, booking: response.booking, count: response.count }));
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.fetchBooking.bookingActions.failure(error));
    }
}

export function* createBookingRequestFunction(action: BookingAction): Generator<any, void, Response> {
    const { id } = action.payload;
    let response: any = null;
    try {
        yield delay(100);
        response = yield confirmBooking(id);
        if (!response || response?.error) {
            yield put(Actions.booking.createBookingRequestAction.failure({ message: response.error }));
        }
        yield put(Actions.booking.createBookingRequestAction.success({ success: response.success }));
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.booking.createBookingRequestAction.failure(error));
    }
}

export function* updateBookingHoldRequestFunction(action: UpdateBooking): Generator<any, void, Response> {
    const { bookingData } = action.payload;
    let response: any = null;
    try {
        yield delay(100);
        response = yield updateBookingHold(bookingData);
        if (!response || response?.errors) {
            yield put(Actions.booking.updateBookingHoldRequestAction.failure({ message: response.errors }));
        }
        if (response && !response.errors) {
            yield put(Actions.booking.updateBookingHoldRequestAction.success());
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.booking.updateBookingHoldRequestAction.failure(error));
    }
}

export function* updateBookingRequestFunction(action: UpdateBooking): Generator<any, void, Response> {
    const { bookingData } = action.payload;
    let response: any = null;
    try {
        yield delay(100);
        response = yield updateBooking(bookingData);
        if (!response || response?.errors) {
            yield put(Actions.booking.updateBookingRequestAction.failure({ message: response.errors }));
        }
        if (response && !response.errors) {
            yield put(Actions.booking.updateBookingRequestAction.success());
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.booking.updateBookingRequestAction.failure(error));
    }
}

export function* fetchAmenitiesListRequestFunction(action: FetchAmenitiesActions): Generator<any, void, Response> {
    const { offset } = action.payload;
    let response: any = null;
    try {
        yield delay(100);
        response = yield fetchAllAmenities(offset ?? 0);
        if (!response || response?.error) {
            yield put(Actions.fetchAmenities.amenityActions.failure({ message: response.error }));
        }
        yield put(Actions.fetchAmenities.amenityActions.success({ amenitiesList: response, count: response.count }));
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.fetchAmenities.amenityActions.failure(error));
    }
}

export function* holdBookingFunction(action: HoldBookingActions): Generator<any, void, Response> {
    const { bookingData } = action.payload;
    let response: any = null;
    try {
        yield delay(100);
        response = yield holdBooking(bookingData);
        if (!response || response?.errors) {
            yield put(Actions.booking.createBookingHoldRequestAction.failure({ message: response.errors }));
        }
        if (response && !response.errors) {
            yield put(Actions.booking.createBookingHoldRequestAction.success({ holdId: response.id }));
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.booking.createBookingHoldRequestAction.failure(error));
    }
}

export function* deleteBookingRequestFunction(action: BookingAction): Generator<any, void, Response> {
    const { id } = action.payload;
    let response: any = null;
    try {
        yield delay(100);
        response = yield deleteBookingHold(id);
        if (!response || response?.error) {
            yield put(Actions.deleteBooking.deleteBookingHoldRequestAction.failure({ message: response.error }));
        }
        yield put(Actions.deleteBooking.deleteBookingHoldRequestAction.success({ success: response }));
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.deleteBooking.deleteBookingHoldRequestAction.failure(error));
    }
}

export function* fetchBlockedSlotsFunction(action: FetchBlockedSlots): Generator<any, void, Response> {
    const { amenity_id, start_date } = action.payload;
    let response: any = null;
    if (!amenity_id || !start_date) return;
    try {
        yield delay(100);
        response = yield fetchBlockedSlots(amenity_id, start_date);
        if (!response || response?.error) {
            yield put(Actions.fetchAmenities.blockedSlotActions.failure({ message: response.error }));
        }
        yield put(Actions.fetchAmenities.blockedSlotActions.success({ slots: response.slots }));
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.fetchAmenities.blockedSlotActions.failure(error));
    }
}

export function* approveBookingFunction(action: ApproveBookingAction): Generator<any, void, Response> {
    const { booking_id } = action.payload;
    let response: any = null;
    try {
        yield delay(100);
        response = yield approveBooking(booking_id);
        if (!response || response?.error) {
            yield put(Actions.booking.approveBookingRequestAction.failure({ message: response.error }));
        }
        yield put(Actions.booking.approveBookingRequestAction.success());
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.booking.approveBookingRequestAction.failure(error));
    }
}

export function* rejectBookingFunction(action: ApproveBookingAction): Generator<any, void, Response> {
    const { booking_id } = action.payload;
    let response: any = null;
    try {
        yield delay(100);
        response = yield rejectBooking(booking_id);
        if (!response || response?.error) {
            yield put(Actions.booking.rejectBookingRequestAction.failure({ message: response.error }));
        } else {
            yield put(Actions.booking.rejectBookingRequestAction.success());
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.booking.rejectBookingRequestAction.failure(error));
    }
}

export function* cancelBookingFunction(action: ApproveBookingAction): Generator<any, void, Response> {
    const { booking_id } = action.payload;
    let response: any = null;
    if (!booking_id) return;
    try {
        yield delay(100);
        response = yield cancelBooking(booking_id);
        if (!response || response?.error) {
            yield put(Actions.booking.cancelBookingRequestAction.failure({ message: response.error }));
        } else {
            yield put(Actions.booking.cancelBookingRequestAction.success());
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.booking.cancelBookingRequestAction.failure(error));
    }
}

export function* fetchBookingCommentsRequestFunction(action: FetchBookingAction): Generator<any, void, Response> {
    const { id } = action.payload;
    if (!id) return;
    let response: any = null;
    try {
        yield delay(100);
        response = yield fetchBookingComments(id);
        if (!response || response?.error) {
            yield put(Actions.fetchBooking.bookingCommentActions.failure({ message: response.error }));
        } else {
            yield put(Actions.fetchBooking.bookingCommentActions.success({ comments: response.comments }));
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.fetchBooking.bookingCommentActions.failure(error));
    }
}

export function* createBookingCommentRequestFunction(action: BookingComment): Generator<any, void, Response> {
    const { comment } = action.payload;
    if (!comment) return;
    let response: any = null;
    try {
        yield delay(100);
        response = yield createBookingComment(comment);
        if (!response || response?.error) {
            yield put(Actions.booking.createBookingCommentRequestAction.failure({ message: response.error }));
        } else {
            yield put(Actions.booking.createBookingCommentRequestAction.success());
            yield put(Actions.fetchViolation.violationCommentActions.request({
                id: comment.resource_id
            }));
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.booking.createBookingCommentRequestAction.failure(error));
    }
}

export function* deleteBookingCommentRequestFunction(action: DeleteBookingComment): Generator<any, void, Response> {
    const { comment_id } = action.payload;
    if (!comment_id) return;
    let response: any = null;
    try {
        yield delay(100);
        response = yield deleteBookingComment(comment_id);
        if (!response || response?.error) {
            yield put(Actions.booking.deleteBookingCommentRequestAction.failure({ message: response.error }));
        } else {
            yield put(Actions.booking.deleteBookingCommentRequestAction.success());
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.booking.deleteBookingCommentRequestAction.failure(error));
    }
}



export default function* bookingSaga() {
    yield takeLatest(Types.FETCH_BOOKING_REQUEST, fetchBookingRequestFunction);
    yield takeLatest(Types.CREATE_BOOKING_REQUEST, createBookingRequestFunction);
    yield takeLatest(Types.UPDATE_BOOKING_HOLD_REQUEST, updateBookingHoldRequestFunction);
    yield takeLatest(Types.UPDATE_BOOKING_REQUEST, updateBookingRequestFunction);
    yield takeLatest(Types.FETCH_BLOCKED_SLOT_REQUEST, fetchBlockedSlotsFunction);
    yield takeLatest(Types.FETCH_AMENITY_LIST_REQUEST, fetchAmenitiesListRequestFunction);
    yield takeLatest(Types.CREATE_BOOKING_HOLD_REQUEST, holdBookingFunction);
    yield takeLatest(Types.DELETE_BOOKING_HOLD_REQUEST, deleteBookingRequestFunction)
    yield takeLatest(Types.APPROVE_BOOKING_REQUEST, approveBookingFunction)
    yield takeLatest(Types.FETCH_BOOKING_COMMENT_REQUEST, fetchBookingCommentsRequestFunction);
    yield takeLatest(Types.CREATE_BOOKING_COMMENT_REQUEST, createBookingCommentRequestFunction);
    yield takeLatest(Types.DELETE_BOOKING_COMMENT_REQUEST, deleteBookingCommentRequestFunction);
    yield takeLatest(Types.CANCEL_BOOKING_REQUEST, cancelBookingFunction);
    yield takeLatest(Types.REJECT_BOOKING_REQUEST, rejectBookingFunction);
}
